<script setup>
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue"
import { computed, ref, onMounted } from "vue"
import { usePage, Link } from "@inertiajs/vue3"
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

const props = defineProps({
    stats: {
        type: Object,
        required: true
    },
    recentPairings: {
        type: Array,
        default: () => []
    },
    binaryEnabled: {
        type: Boolean,
        default: false
    },
    weeklyData: {
        type: Array,
        default: () => []
    },
    monthlyData: {
        type: Array,
        default: () => []
    }
})

const page = usePage()
const currencySymbol = computed(() => page.props.currencySymbol || '$')
const primaryColor = computed(() => page.props.primaryColor || '#3b82f6')

// Chart refs
const volumeTrendChart = ref(null)
const legBalanceChart = ref(null)
const pairingTrendChart = ref(null)
let volumeChartInstance = null
let legChartInstance = null
let pairingChartInstance = null

const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol.value}0.00`
    const num = parseFloat(value) || 0
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num)
}

const formatNumber = (value) => {
    if (value === null || value === undefined) return '0'
    return new Intl.NumberFormat('en-US').format(value)
}

const getRelativeTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
    return date.toLocaleDateString()
}

const getPercentageColor = (percentage) => {
    if (percentage >= 70) return 'text-green-600 dark:text-green-400'
    if (percentage >= 40) return 'text-yellow-600 dark:text-yellow-400'
    return 'text-red-600 dark:text-red-400'
}

const balanceStatus = computed(() => {
    if (!props.stats) return { label: 'N/A', color: 'text-gray-400', bgColor: 'bg-gray-500/20' }
    const diff = Math.abs((props.stats.left_percentage || 0) - (props.stats.right_percentage || 0))
    if (diff <= 20) return { label: 'Excellent', color: 'bi-bal--green',  acRgb: '34,197,94' }
    if (diff <= 40) return { label: 'Good',      color: 'bi-bal--blue',   acRgb: '59,130,246' }
    if (diff <= 60) return { label: 'Fair',      color: 'bi-bal--amber',  acRgb: '251,191,36' }
    return                 { label: 'Needs Balance', color: 'bi-bal--red', acRgb: '239,68,68' }
})

const dailyLimitPercentage = computed(() => {
    return Math.min(((props.stats.today_pairs || 0) / (props.stats.daily_limit || 1)) * 100, 100)
})

const projectedMonthlyEarnings = computed(() => {
    const avgDaily = (props.stats.total_binary_bonus || 0) / Math.max(props.stats.days_active || 1, 1)
    return avgDaily * 30
})

const achievements = computed(() => {
    const results = []
    const totalPairs = props.stats.total_pairs || 0
    const totalBonus = props.stats.total_binary_bonus || 0

    if (totalPairs >= 1) results.push({ icon: '🎯', title: 'First Pair', desc: 'Earned your first pairing bonus', level: 'bronze' })
    if (totalPairs >= 10) results.push({ icon: '⭐', title: 'Rising Star', desc: '10+ successful pairings', level: 'silver' })
    if (totalPairs >= 50) results.push({ icon: '💎', title: 'Diamond Achiever', desc: '50+ successful pairings', level: 'gold' })
    if (totalPairs >= 100) results.push({ icon: '👑', title: 'Elite Earner', desc: '100+ successful pairings', level: 'platinum' })
    if (totalBonus >= 1000) results.push({ icon: '💰', title: 'Millionaire Track', desc: '$1000+ in binary bonuses', level: 'gold' })

    return results.slice(0, 4) // Show top 4 achievements
})

const initVolumeTrendChart = () => {
    if (!volumeTrendChart.value) return

    try {
        const ctx = volumeTrendChart.value.getContext('2d')
        const weekData = props.weeklyData || []

        const gradient1 = ctx.createLinearGradient(0, 0, 0, 300)
        gradient1.addColorStop(0, 'rgba(34, 197, 94, 0.3)')
        gradient1.addColorStop(1, 'rgba(34, 197, 94, 0.0)')

        const gradient2 = ctx.createLinearGradient(0, 0, 0, 300)
        gradient2.addColorStop(0, 'rgba(168, 85, 247, 0.3)')
        gradient2.addColorStop(1, 'rgba(168, 85, 247, 0.0)')

        volumeChartInstance = new Chart(ctx, {
            type: 'line',
            data: {
                labels: weekData.map(d => d.date || ''),
                datasets: [
                    {
                        label: 'Left Leg',
                        data: weekData.map(d => d.left_volume || 0),
                        borderColor: 'rgb(34, 197, 94)',
                        backgroundColor: gradient1,
                        borderWidth: 2,
                        fill: true,
                        tension: 0.4
                    },
                    {
                        label: 'Right Leg',
                        data: weekData.map(d => d.right_volume || 0),
                        borderColor: 'rgb(168, 85, 247)',
                        backgroundColor: gradient2,
                        borderWidth: 2,
                        fill: true,
                        tension: 0.4
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        labels: { color: '#94a3b8' }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(15, 23, 42, 0.95)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        borderColor: 'rgba(148, 163, 184, 0.2)',
                        borderWidth: 1
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(148, 163, 184, 0.1)' },
                        ticks: { color: '#94a3b8' }
                    },
                    x: {
                        grid: { color: 'rgba(148, 163, 184, 0.1)' },
                        ticks: { color: '#94a3b8' }
                    }
                }
            }
        })
    } catch (error) {
        console.error('Error initializing volume trend chart:', error)
    }
}

const initLegBalanceChart = () => {
    if (!legBalanceChart.value) return

    try {
        const ctx = legBalanceChart.value.getContext('2d')

        legChartInstance = new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: ['Left Leg', 'Right Leg'],
                datasets: [{
                    data: [props.stats.left_leg_volume || 0, props.stats.right_leg_volume || 0],
                    backgroundColor: ['rgba(34, 197, 94, 0.8)', 'rgba(168, 85, 247, 0.8)'],
                    borderColor: ['rgb(34, 197, 94)', 'rgb(168, 85, 247)'],
                    borderWidth: 2
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: { color: '#94a3b8', padding: 15 }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(15, 23, 42, 0.95)',
                        titleColor: '#fff',
                        bodyColor: '#fff'
                    }
                }
            }
        })
    } catch (error) {
        console.error('Error initializing leg balance chart:', error)
    }
}

const initPairingTrendChart = () => {
    if (!pairingTrendChart.value) return

    try {
        const ctx = pairingTrendChart.value.getContext('2d')
        const monthData = props.monthlyData || []

        const gradient = ctx.createLinearGradient(0, 0, 0, 250)
        gradient.addColorStop(0, 'rgba(59, 130, 246, 0.5)')
        gradient.addColorStop(1, 'rgba(59, 130, 246, 0.0)')

        pairingChartInstance = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: monthData.map(d => d.date || ''),
                datasets: [{
                    label: 'Pairings',
                    data: monthData.map(d => d.pairs_count || 0),
                    backgroundColor: gradient,
                    borderColor: 'rgb(59, 130, 246)',
                    borderWidth: 2,
                    borderRadius: 6
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: 'rgba(15, 23, 42, 0.95)',
                        titleColor: '#fff',
                        bodyColor: '#fff'
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(148, 163, 184, 0.1)' },
                        ticks: { color: '#94a3b8', precision: 0 }
                    },
                    x: {
                        grid: { display: false },
                        ticks: { color: '#94a3b8' }
                    }
                }
            }
        })
    } catch (error) {
        console.error('Error initializing pairing trend chart:', error)
    }
}

onMounted(() => {
    if (props.binaryEnabled) {
        setTimeout(() => {
            initVolumeTrendChart()
            initLegBalanceChart()
            initPairingTrendChart()
        }, 100)
    }
})
</script>

<template>
    <UserLayout :page-title="'Binary MLM'" :page-section="'Dashboard'">
        <div class="bi-page">

            <!-- Welcome strip -->
            <div class="bi-welcome anim-up" style="--d:0ms">
                <div class="biw-left">
                    <div class="biw-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"/>
                        </svg>
                    </div>
                    <div class="biw-text">
                        <span class="biw-eye">Binary MLM</span>
                        <h1 class="biw-title">Dashboard</h1>
                    </div>
                </div>
                <div class="biw-chips">
                    <div class="biw-chip biw-chip--purple">
                        <span class="biw-chip-lbl">Position</span>
                        <span class="biw-chip-val">{{ stats.binary_position || 'Not Placed' }}</span>
                    </div>
                    <div class="biw-chip biw-chip--green">
                        <span class="biw-chip-lbl">Pairs</span>
                        <span class="biw-chip-val">{{ formatNumber(stats.total_pairs) }}</span>
                    </div>
                    <div class="biw-chip biw-chip--amber">
                        <span class="biw-chip-lbl">Earned</span>
                        <span class="biw-chip-val">{{ currencySymbol }}{{ formatCurrency(stats.total_binary_bonus) }}</span>
                    </div>
                    <Link href="/user/binary/genealogy" class="biw-tree-btn">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                        </svg>
                        View Tree
                    </Link>
                </div>
            </div>

            <!-- Not enabled warning -->
            <div v-if="!binaryEnabled" class="bi-warn anim-up" style="--d:60ms">
                <div class="bi-warn-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    </svg>
                </div>
                <div>
                    <h3 class="bi-warn-title">Binary MLM Not Enabled</h3>
                    <p class="bi-warn-desc">The Binary MLM system is currently disabled by the administrator.</p>
                </div>
            </div>

            <template v-else>

                <!-- 4 stat cards -->
                <div class="bi-stats anim-up" style="--d:60ms">
                    <!-- Position -->
                    <div class="bi-stat" style="--ac:59,130,246">
                        <div class="bi-glow"></div>
                        <div class="bi-stat-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                        </div>
                        <div class="bi-stat-val bi-stat-val--text">{{ stats.binary_position || 'Not Placed' }}</div>
                        <div class="bi-stat-lbl">Binary Position</div>
                        <div class="bi-stat-sub">Under parent network</div>
                    </div>
                    <!-- Left Leg -->
                    <div class="bi-stat" style="--ac:34,197,94">
                        <div class="bi-glow"></div>
                        <div class="bi-stat-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11l5-5m0 0l5 5m-5-5v12"/>
                            </svg>
                        </div>
                        <div class="bi-stat-chip">{{ stats.left_percentage }}%</div>
                        <div class="bi-stat-val">{{ currencySymbol }}{{ formatCurrency(stats.left_leg_volume) }}</div>
                        <div class="bi-stat-lbl">Left Leg Volume</div>
                        <div class="bi-stat-sub">Carry: {{ currencySymbol }}{{ formatCurrency(stats.left_leg_carry_forward || 0) }}</div>
                    </div>
                    <!-- Right Leg -->
                    <div class="bi-stat" style="--ac:168,85,247">
                        <div class="bi-glow"></div>
                        <div class="bi-stat-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13l-5 5m0 0l-5-5m5 5V6"/>
                            </svg>
                        </div>
                        <div class="bi-stat-chip">{{ stats.right_percentage }}%</div>
                        <div class="bi-stat-val">{{ currencySymbol }}{{ formatCurrency(stats.right_leg_volume) }}</div>
                        <div class="bi-stat-lbl">Right Leg Volume</div>
                        <div class="bi-stat-sub">Carry: {{ currencySymbol }}{{ formatCurrency(stats.right_leg_carry_forward || 0) }}</div>
                    </div>
                    <!-- Total Earnings -->
                    <div class="bi-stat" style="--ac:251,191,36">
                        <div class="bi-glow"></div>
                        <div class="bi-stat-icon">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <div class="bi-stat-val">{{ currencySymbol }}{{ formatCurrency(stats.total_binary_bonus) }}</div>
                        <div class="bi-stat-lbl">Binary Earnings</div>
                        <div class="bi-stat-sub">All-time earnings</div>
                    </div>
                </div>

                <!-- Charts row -->
                <div class="bi-charts-row anim-up" style="--d:120ms">
                    <!-- Volume trend (2/3) -->
                    <div class="bi-card bi-chart-main">
                        <div class="bi-card-head">
                            <div>
                                <h3 class="bi-card-title">Volume Trend Analysis</h3>
                                <p class="bi-card-sub">7-day leg volume comparison</p>
                            </div>
                            <div class="bi-badge-pill">
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
                                </svg>
                                Analytics
                            </div>
                        </div>
                        <div class="bi-chart-wrap">
                            <canvas ref="volumeTrendChart"></canvas>
                        </div>
                    </div>
                    <!-- Leg balance donut (1/3) -->
                    <div class="bi-card bi-chart-side">
                        <div class="bi-card-head">
                            <div>
                                <h3 class="bi-card-title">Leg Distribution</h3>
                                <p class="bi-card-sub">Current balance status</p>
                            </div>
                        </div>
                        <div class="bi-donut-wrap">
                            <canvas ref="legBalanceChart"></canvas>
                        </div>
                        <div class="bi-balance-status" :class="balanceStatus.color">
                            <div class="bi-bs-row">
                                <span class="bi-bs-label">Balance Status</span>
                                <span class="bi-bs-val">{{ balanceStatus.label }}</span>
                            </div>
                            <p class="bi-bs-diff">
                                {{ Math.abs(stats.left_percentage - stats.right_percentage).toFixed(1) }}% difference between legs
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Achievements + Daily limit -->
                <div class="bi-insights anim-up" style="--d:180ms">
                    <!-- Achievements -->
                    <div class="bi-card">
                        <div class="bi-card-head">
                            <div class="bi-ach-icon">
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                            </div>
                            <div>
                                <h3 class="bi-card-title">Achievements</h3>
                                <p class="bi-card-sub">Your milestones</p>
                            </div>
                        </div>
                        <div v-if="achievements.length === 0" class="bi-empty">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"/>
                            </svg>
                            <p>Start earning to unlock achievements</p>
                        </div>
                        <div v-else class="bi-ach-grid">
                            <div v-for="(ach, idx) in achievements" :key="idx" class="bi-ach-item">
                                <div class="bi-ach-dot" :class="`bi-ach-dot--${ach.level}`"></div>
                                <div class="bi-ach-emoji">{{ ach.icon }}</div>
                                <h4 class="bi-ach-title">{{ ach.title }}</h4>
                                <p class="bi-ach-desc">{{ ach.desc }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Daily limit + insights -->
                    <div class="bi-card">
                        <div class="bi-card-head">
                            <div>
                                <h3 class="bi-card-title">Daily Pairing Limit</h3>
                                <p class="bi-card-sub">Today's progress</p>
                            </div>
                            <div class="bi-dl-counter">
                                <span class="bi-dl-num">{{ stats.today_pairs }}</span>
                                <span class="bi-dl-denom">/ {{ stats.daily_limit }}</span>
                            </div>
                        </div>
                        <div class="bi-progress-wrap">
                            <div class="bi-progress-track">
                                <div class="bi-progress-fill bi-progress-fill--blue"
                                    :style="{ width: dailyLimitPercentage + '%' }"></div>
                            </div>
                            <p class="bi-progress-pct">{{ dailyLimitPercentage.toFixed(1) }}% of daily limit used</p>
                        </div>

                        <div class="bi-divider"></div>

                        <div class="bi-card-sub-head">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                            </svg>
                            Performance Insights
                        </div>
                        <div class="bi-insights-list">
                            <div class="bi-insight-row">
                                <span>Projected Monthly</span>
                                <strong class="bi-ins-green">{{ currencySymbol }}{{ formatCurrency(projectedMonthlyEarnings) }}</strong>
                            </div>
                            <div class="bi-insight-row">
                                <span>Total Pairs</span>
                                <strong>{{ formatNumber(stats.total_pairs) }}</strong>
                            </div>
                            <div class="bi-insight-row">
                                <span>Avg Per Pair</span>
                                <strong class="bi-ins-blue">
                                    {{ currencySymbol }}{{ formatCurrency((stats.total_binary_bonus || 0) / Math.max(stats.total_pairs || 1, 1)) }}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pairing trend + Leg balance analysis -->
                <div class="bi-bottom anim-up" style="--d:240ms">
                    <!-- Monthly pairing trend -->
                    <div class="bi-card">
                        <div class="bi-card-head">
                            <div>
                                <h3 class="bi-card-title">Monthly Pairing Trend</h3>
                                <p class="bi-card-sub">30-day activity overview</p>
                            </div>
                        </div>
                        <div class="bi-trend-wrap">
                            <canvas ref="pairingTrendChart"></canvas>
                        </div>
                    </div>

                    <!-- Leg balance analysis -->
                    <div class="bi-card">
                        <div class="bi-card-head">
                            <h3 class="bi-card-title">Leg Balance Analysis</h3>
                        </div>
                        <div class="bi-legs">
                            <!-- Left leg bar -->
                            <div class="bi-leg-row">
                                <div class="bi-leg-info">
                                    <div class="bi-leg-dot bi-leg-dot--green"></div>
                                    <span class="bi-leg-name">Left Leg</span>
                                    <span class="bi-leg-amount">{{ currencySymbol }}{{ formatNumber(stats.left_leg_volume) }}</span>
                                </div>
                                <div class="bi-leg-track">
                                    <div class="bi-leg-fill bi-leg-fill--green" :style="{ width: stats.left_percentage + '%' }"></div>
                                </div>
                                <div class="bi-leg-meta">
                                    <span class="bi-leg-pct bi-leg-pct--green">{{ stats.left_percentage }}%</span>
                                    <span class="bi-leg-carry">Carry: {{ currencySymbol }}{{ formatCurrency(stats.left_leg_carry_forward || 0) }}</span>
                                </div>
                            </div>
                            <!-- Right leg bar -->
                            <div class="bi-leg-row">
                                <div class="bi-leg-info">
                                    <div class="bi-leg-dot bi-leg-dot--purple"></div>
                                    <span class="bi-leg-name">Right Leg</span>
                                    <span class="bi-leg-amount">{{ currencySymbol }}{{ formatNumber(stats.right_leg_volume) }}</span>
                                </div>
                                <div class="bi-leg-track">
                                    <div class="bi-leg-fill bi-leg-fill--purple" :style="{ width: stats.right_percentage + '%' }"></div>
                                </div>
                                <div class="bi-leg-meta">
                                    <span class="bi-leg-pct bi-leg-pct--purple">{{ stats.right_percentage }}%</span>
                                    <span class="bi-leg-carry">Carry: {{ currencySymbol }}{{ formatCurrency(stats.right_leg_carry_forward || 0) }}</span>
                                </div>
                            </div>
                            <!-- Balance rating -->
                            <div class="bi-bal-row">
                                <div class="bi-bal-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                                    </svg>
                                </div>
                                <div>
                                    <p class="bi-bal-sub">Balance Rating</p>
                                    <p class="bi-bal-label">{{ balanceStatus.label }}</p>
                                </div>
                                <div class="bi-bal-diff" :class="balanceStatus.color">
                                    {{ Math.abs(stats.left_percentage - stats.right_percentage).toFixed(1) }}%
                                    <small>Diff</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Recent pairings + Quick actions -->
                <div class="bi-pairings-row anim-up" style="--d:300ms">
                    <!-- Recent pairings (2/3) -->
                    <div class="bi-card bi-pairings-main">
                        <div class="bi-card-head">
                            <div>
                                <h3 class="bi-card-title">Recent Pairings</h3>
                                <p class="bi-card-sub">Latest binary matching rewards</p>
                            </div>
                            <Link href="/user/binary/pairings" class="bi-view-all">
                                View All
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                                </svg>
                            </Link>
                        </div>
                        <div v-if="recentPairings.length === 0" class="bi-empty bi-empty--lg">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
                            </svg>
                            <p>No pairings yet</p>
                            <small>Your binary pairings will appear here</small>
                        </div>
                        <div v-else class="bi-pairings-list">
                            <div v-for="pairing in recentPairings" :key="pairing.id" class="bi-pairing-item">
                                <div class="bi-pair-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                </div>
                                <div class="bi-pair-body">
                                    <div class="bi-pair-row1">
                                        <span class="bi-pair-time">{{ getRelativeTime(pairing.created_at) }}</span>
                                        <span class="bi-pair-badge">Matched</span>
                                    </div>
                                    <div class="bi-pair-legs">
                                        <span class="bi-pair-leg bi-pair-leg--green">
                                            <span class="bi-leg-dot bi-leg-dot--green"></span>
                                            Left: {{ currencySymbol }}{{ formatNumber(pairing.left_volume) }}
                                        </span>
                                        <span class="bi-pair-sep"></span>
                                        <span class="bi-pair-leg bi-pair-leg--purple">
                                            <span class="bi-leg-dot bi-leg-dot--purple"></span>
                                            Right: {{ currencySymbol }}{{ formatNumber(pairing.right_volume) }}
                                        </span>
                                    </div>
                                </div>
                                <div class="bi-pair-reward">
                                    <div class="bi-pair-amt">+{{ currencySymbol }}{{ formatCurrency(pairing.reward_amount) }}</div>
                                    <div class="bi-pair-cnt">{{ pairing.pairs_count }} pair{{ pairing.pairs_count > 1 ? 's' : '' }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Quick actions (1/3) -->
                    <div class="bi-card bi-qa-card">
                        <div class="bi-card-head">
                            <h3 class="bi-card-title">Quick Actions</h3>
                        </div>
                        <nav class="bi-qa-nav">
                            <Link href="/user/binary/genealogy" class="bi-qa-item bi-qa-item--blue">
                                <div class="bi-qa-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                                    </svg>
                                </div>
                                <div class="bi-qa-text">
                                    <h4>View Genealogy</h4>
                                    <p>Your binary tree</p>
                                </div>
                                <svg class="bi-qa-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                </svg>
                            </Link>
                            <Link href="/user/binary/pairings" class="bi-qa-item bi-qa-item--purple">
                                <div class="bi-qa-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                    </svg>
                                </div>
                                <div class="bi-qa-text">
                                    <h4>Pairing History</h4>
                                    <p>View all pairings</p>
                                </div>
                                <svg class="bi-qa-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                </svg>
                            </Link>
                            <Link href="/user/binary/wallet" class="bi-qa-item bi-qa-item--green">
                                <div class="bi-qa-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                                    </svg>
                                </div>
                                <div class="bi-qa-text">
                                    <h4>Binary Wallet</h4>
                                    <p>Token & credit balances</p>
                                </div>
                                <svg class="bi-qa-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                </svg>
                            </Link>
                        </nav>
                    </div>
                </div>

            </template>
        </div>
    </UserLayout>
</template>

<style scoped>
/* ── animations ─────────────────────────────────────────────────── */
@keyframes slideUp {
    from { opacity:0; transform:translateY(22px) }
    to   { opacity:1; transform:none }
}
.anim-up {
    animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both;
    animation-delay: var(--d,0ms);
}
@keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:.9} }

/* ── page ───────────────────────────────────────────────────────── */
.bi-page {
    max-width: 1280px;
    margin: 0 auto;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    z-index: 10;
    width: 100%;
}
@media(min-width:640px)  { .bi-page { padding: 18px 18px; gap: 14px; } }
@media(min-width:1024px) { .bi-page { padding: 20px 24px; gap: 16px; } }

/* ── welcome strip ──────────────────────────────────────────────── */
.bi-welcome {
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
    padding:12px 16px; border-radius:13px;
    background:rgba(167,93,237,0.06);
    border:1px solid rgba(167,93,237,0.16);
}
.biw-left { display:flex; align-items:center; gap:10px; flex-shrink:0; }
.biw-icon {
    width:40px; height:40px; border-radius:11px;
    background:linear-gradient(135deg,rgba(124,58,237,0.3),rgba(167,93,237,0.2));
    border:1px solid rgba(167,93,237,0.28);
    display:flex; align-items:center; justify-content:center; flex-shrink:0; color:#a75ded;
    box-shadow:0 0 14px rgba(167,93,237,0.22);
}
.biw-icon svg { width:18px; height:18px; }
.biw-eye   { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; color:rgba(167,93,237,0.7); display:block; }
.biw-title { font-size:15px; font-weight:900; color:#fff; letter-spacing:-0.02em; margin:0; }
.biw-chips { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.biw-chip {
    display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:6px 12px; border-radius:9px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    min-width:58px;
}
.biw-chip-lbl { font-size:8px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.12em; }
.biw-chip-val { font-size:13px; font-weight:900; color:#fff; }
.biw-chip--purple .biw-chip-val { color:#c084fc; }
.biw-chip--green  .biw-chip-val { color:#4ade80; }
.biw-chip--amber  .biw-chip-val { color:#fbbf24; }
.biw-tree-btn {
    display:inline-flex; align-items:center; gap:4px;
    padding:6px 12px; border-radius:9px;
    background:rgba(59,130,246,0.1); border:1px solid rgba(59,130,246,0.22);
    color:#60a5fa; font-size:11px; font-weight:700; text-decoration:none;
    transition:all .2s;
}
.biw-tree-btn:hover { background:rgba(59,130,246,0.2); border-color:rgba(59,130,246,0.38); }
.biw-tree-btn svg { width:14px; height:14px; }

/* ── warn ───────────────────────────────────────────────────────── */
.bi-warn {
    display:flex; align-items:center; gap:1rem;
    padding:1.25rem 1.5rem; border-radius:1rem;
    background:rgba(251,191,36,0.08); border:1px solid rgba(251,191,36,0.28);
}
.bi-warn-icon {
    width:2.5rem; height:2.5rem; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
    background:rgba(251,191,36,0.15); border-radius:.625rem; color:#fbbf24;
}
.bi-warn-icon svg { width:1.25rem; height:1.25rem; }
.bi-warn-title { font-size:13px; font-weight:800; color:#fbbf24; }
.bi-warn-desc  { font-size:11px; color:#374151; margin-top:2px; }

/* ── stat cards ─────────────────────────────────────────────────── */
.bi-stats {
    display:grid; grid-template-columns:repeat(2,1fr); gap:10px;
}
@media (min-width:768px) { .bi-stats { grid-template-columns:repeat(4,1fr); } }

.bi-stat {
    position:relative;
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(var(--ac),.22); border-radius:14px;
    padding:12px; overflow:hidden;
    transition:transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
    cursor:default;
}
.bi-stat:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(var(--ac),0.18); }
.bi-stat::before {
    content:''; position:absolute; top:0; left:0; right:0; height:1px;
    background:linear-gradient(90deg,transparent,rgba(var(--ac),.5),transparent);
}
.bi-glow {
    position:absolute; top:-20px; right:-20px; width:100px; height:100px;
    border-radius:50%; background:radial-gradient(circle,rgba(var(--ac),.22) 0%,transparent 70%);
    pointer-events:none;
}
.bi-stat-icon {
    width:30px; height:30px; border-radius:9px;
    background:rgba(var(--ac),.15); border:1px solid rgba(var(--ac),.28);
    display:flex; align-items:center; justify-content:center;
    margin-bottom:8px; color:rgb(var(--ac));
}
.bi-stat-icon svg { width:15px; height:15px; }
.bi-stat-chip {
    display:inline-flex; padding:2px 8px; border-radius:9999px;
    font-size:10px; font-weight:700;
    background:rgba(var(--ac),.15); color:rgb(var(--ac));
    margin-bottom:4px;
}
.bi-stat-val { font-size:18px; font-weight:900; color:#fff; letter-spacing:-0.03em; margin-bottom:3px; line-height:1.1; word-break:break-all; }
.bi-stat-val--text { font-size:15px; text-transform:capitalize; }
.bi-stat-lbl { font-size:10px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.06em; line-height:1.2; }
.bi-stat-sub { font-size:10px; color:#374151; margin-top:2px; }
@media(min-width:640px) { .bi-stat { padding:14px; } .bi-stat-val { font-size:20px; } }

/* ── card base ──────────────────────────────────────────────────── */
.bi-card {
    background:rgba(10,4,22,0.97);
    border:1px solid rgba(167,93,237,0.13); border-radius:16px;
    overflow:hidden; position:relative;
    transition:border-color 0.25s;
}
.bi-card::before {
    content:''; position:absolute; top:0; left:14%; right:14%; height:1px;
    background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.bi-card:hover { border-color:rgba(167,93,237,0.24); }
.bi-card-head {
    display:flex; align-items:center; justify-content:space-between;
    padding:14px 14px 10px; gap:10px;
}
.bi-card-title { font-size:13px; font-weight:800; color:#fff; letter-spacing:-0.01em; }
.bi-card-sub   { font-size:10px; color:#374151; margin-top:1px; }

.bi-badge-pill {
    display:inline-flex; align-items:center; gap:5px;
    padding:5px 10px; border-radius:8px;
    background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1);
    font-size:10px; font-weight:700; color:rgba(255,255,255,0.55);
}
.bi-badge-pill svg { width:13px; height:13px; }

/* ── charts row ─────────────────────────────────────────────────── */
.bi-charts-row {
    display:grid; grid-template-columns:1fr; gap:12px;
}
@media (min-width:1200px) { .bi-charts-row { grid-template-columns:2fr 1fr; } }

.bi-chart-wrap { padding:0 14px 14px; height:20rem; }
.bi-donut-wrap { padding:0 14px; height:16rem; display:flex; align-items:center; justify-content:center; }

.bi-balance-status {
    margin:0 14px 14px; padding:12px;
    border-radius:11px; border:1px solid rgba(255,255,255,0.08);
    background:rgba(255,255,255,0.03);
}
.bi-bs-row { display:flex; align-items:center; justify-content:space-between; }
.bi-bs-label { font-size:.78rem; font-weight:600; color:rgba(255,255,255,0.5); }
.bi-bs-val   { font-size:1.1rem; font-weight:800; }
.bi-bs-diff  { font-size:.72rem; color:rgba(255,255,255,0.35); margin-top:.35rem; }
.bi-bal--green .bi-bs-val  { color:#4ade80; }
.bi-bal--blue  .bi-bs-val  { color:#60a5fa; }
.bi-bal--amber .bi-bs-val  { color:#fbbf24; }
.bi-bal--red   .bi-bs-val  { color:#f87171; }

/* ── insights row ───────────────────────────────────────────────── */
.bi-insights {
    display:grid; grid-template-columns:1fr; gap:12px;
}
@media (min-width:900px) { .bi-insights { grid-template-columns:1fr 1fr; } }

.bi-ach-icon {
    width:2.25rem; height:2.25rem; border-radius:.625rem;
    background:rgba(251,191,36,0.15); border:1px solid rgba(251,191,36,0.3);
    display:flex; align-items:center; justify-content:center; color:#fbbf24;
}
.bi-ach-icon svg { width:1.1rem; height:1.1rem; }

.bi-ach-grid {
    display:grid; grid-template-columns:1fr 1fr; gap:8px;
    padding:0 14px 14px;
}
.bi-ach-item {
    position:relative; padding:1rem .875rem;
    background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.07);
    border-radius:.875rem; transition:transform .2s;
}
.bi-ach-item:hover { transform:translateY(-2px); }
.bi-ach-dot {
    position:absolute; top:.625rem; right:.625rem;
    width:.5rem; height:.5rem; border-radius:50%;
    animation:pulse 1.5s ease-in-out infinite;
}
.bi-ach-dot--bronze   { background:#b45309; }
.bi-ach-dot--silver   { background:#94a3b8; }
.bi-ach-dot--gold     { background:#fbbf24; }
.bi-ach-dot--platinum { background:#c084fc; }
.bi-ach-emoji  { font-size:1.75rem; margin-bottom:.5rem; line-height:1; }
.bi-ach-title  { font-size:.8rem; font-weight:700; color:#fff; margin-bottom:.2rem; }
.bi-ach-desc   { font-size:.7rem; color:rgba(255,255,255,0.4); line-height:1.4; }

.bi-empty {
    text-align:center; padding:2.5rem 1rem;
    color:rgba(255,255,255,0.3);
    display:flex; flex-direction:column; align-items:center; gap:.875rem;
}
.bi-empty svg { width:3.5rem; height:3.5rem; opacity:.4; }
.bi-empty p   { font-size:.9rem; }
.bi-empty small { font-size:.75rem; opacity:.7; }
.bi-empty--lg { padding:4rem 1rem; }

.bi-dl-counter { text-align:right; }
.bi-dl-num   { font-size:1.75rem; font-weight:800; color:#fff; line-height:1; }
.bi-dl-denom { font-size:.78rem; color:rgba(255,255,255,0.38); }

.bi-progress-wrap { padding:0 14px 12px; }
.bi-progress-track {
    width:100%; height:.875rem; border-radius:9999px;
    background:rgba(255,255,255,0.06); overflow:hidden;
}
.bi-progress-fill {
    height:100%; border-radius:9999px; transition:width .5s ease-out;
    position:relative;
}
.bi-progress-fill::after {
    content:''; position:absolute; inset:0; border-radius:inherit;
    background:rgba(255,255,255,0.2); animation:pulse 1.5s ease-in-out infinite;
}
.bi-progress-fill--blue { background:linear-gradient(90deg,#2563eb,#60a5fa); }
.bi-progress-fill--green { background:linear-gradient(90deg,#16a34a,#4ade80); }
.bi-progress-fill--purple { background:linear-gradient(90deg,#7c3aed,#c084fc); }
.bi-progress-pct { font-size:.72rem; color:rgba(255,255,255,0.38); margin-top:.5rem; text-align:center; }

.bi-divider { height:1px; background:rgba(255,255,255,0.06); margin:0 14px; }
.bi-card-sub-head {
    display:flex; align-items:center; gap:6px;
    padding:12px 14px 6px;
    font-size:11px; font-weight:700; color:rgba(255,255,255,0.7);
}
.bi-card-sub-head svg { width:14px; height:14px; color:#4ade80; }

.bi-insights-list { padding:0 14px 14px; display:flex; flex-direction:column; gap:6px; }
.bi-insight-row {
    display:flex; align-items:center; justify-content:space-between;
    padding:9px 11px; border-radius:10px;
    background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06);
    font-size:11px;
}
.bi-insight-row span { color:rgba(255,255,255,0.5); }
.bi-insight-row strong { color:#fff; }
.bi-ins-green { color:#4ade80 !important; }
.bi-ins-blue  { color:#60a5fa !important; }

/* ── bottom row ─────────────────────────────────────────────────── */
.bi-bottom {
    display:grid; grid-template-columns:1fr; gap:12px;
}
@media (min-width:900px) { .bi-bottom { grid-template-columns:1fr 1fr; } }

.bi-trend-wrap { padding:0 14px 14px; height:16rem; }

.bi-legs { padding:0 14px 14px; display:flex; flex-direction:column; gap:14px; }
.bi-leg-row { display:flex; flex-direction:column; gap:.4rem; }
.bi-leg-info {
    display:flex; align-items:center; gap:.5rem;
    font-size:.83rem;
}
.bi-leg-dot {
    width:.625rem; height:.625rem; border-radius:50%; flex-shrink:0;
}
.bi-leg-dot--green  { background:#22c55e; }
.bi-leg-dot--purple { background:#a855f7; }
.bi-leg-name   { font-weight:600; color:rgba(255,255,255,0.75); flex:1; }
.bi-leg-amount { font-weight:700; color:#fff; }
.bi-leg-track  { width:100%; height:.625rem; border-radius:9999px; background:rgba(255,255,255,0.06); overflow:hidden; }
.bi-leg-fill   { height:100%; border-radius:9999px; transition:width .7s ease-out; }
.bi-leg-fill--green  { background:linear-gradient(90deg,#16a34a,#4ade80); }
.bi-leg-fill--purple { background:linear-gradient(90deg,#7c3aed,#c084fc); }
.bi-leg-meta   { display:flex; justify-content:space-between; align-items:center; }
.bi-leg-pct    { font-size:.72rem; font-weight:700; }
.bi-leg-pct--green  { color:#4ade80; }
.bi-leg-pct--purple { color:#c084fc; }
.bi-leg-carry  { font-size:.68rem; color:rgba(255,255,255,0.35); }

.bi-bal-row {
    display:flex; align-items:center; gap:.875rem;
    padding:.875rem; border-radius:.875rem;
    background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.07);
    margin-top:.25rem;
}
.bi-bal-icon {
    width:2.25rem; height:2.25rem; flex-shrink:0;
    background:rgba(59,130,246,0.15); border:1px solid rgba(59,130,246,0.3);
    border-radius:.625rem; display:flex; align-items:center; justify-content:center; color:#60a5fa;
}
.bi-bal-icon svg { width:1.1rem; height:1.1rem; }
.bi-bal-sub   { font-size:.68rem; color:rgba(255,255,255,0.38); }
.bi-bal-label { font-size:.875rem; font-weight:700; color:#fff; }
.bi-bal-diff {
    margin-left:auto; text-align:right;
    font-size:1.5rem; font-weight:800; line-height:1;
}
.bi-bal-diff small { display:block; font-size:.65rem; font-weight:400; color:rgba(255,255,255,0.35); margin-top:.15rem; }
.bi-bal--green .bi-bal-diff { color:#4ade80; }
.bi-bal--blue  .bi-bal-diff { color:#60a5fa; }
.bi-bal--amber .bi-bal-diff { color:#fbbf24; }
.bi-bal--red   .bi-bal-diff { color:#f87171; }

/* ── pairings row ───────────────────────────────────────────────── */
.bi-pairings-row {
    display:grid; grid-template-columns:1fr; gap:12px;
}
@media (min-width:1100px) { .bi-pairings-row { grid-template-columns:2fr 1fr; } }

.bi-view-all {
    display:inline-flex; align-items:center; gap:.35rem; text-decoration:none;
    padding:.375rem .875rem; border-radius:.5rem;
    background:rgba(59,130,246,0.1); border:1px solid rgba(59,130,246,0.25);
    color:#60a5fa; font-size:.78rem; font-weight:700;
    transition:all .2s;
}
.bi-view-all:hover { background:rgba(59,130,246,0.2); }
.bi-view-all svg { width:.9rem; height:.9rem; }

.bi-pairings-list { display:flex; flex-direction:column; gap:6px; padding:0 14px 14px; }
.bi-pairing-item {
    display:flex; align-items:center; gap:.875rem;
    padding:.875rem; border-radius:.875rem;
    background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06);
    transition:border-color .2s, transform .2s;
}
.bi-pairing-item:hover { border-color:rgba(59,130,246,0.3); transform:translateX(2px); }
.bi-pair-icon {
    width:2.25rem; height:2.25rem; flex-shrink:0;
    background:rgba(59,130,246,0.15); border:1px solid rgba(59,130,246,0.3);
    border-radius:.625rem; display:flex; align-items:center; justify-content:center; color:#60a5fa;
}
.bi-pair-icon svg { width:1.1rem; height:1.1rem; }
.bi-pair-body { flex:1; min-width:0; }
.bi-pair-row1 { display:flex; align-items:center; gap:.5rem; margin-bottom:.35rem; }
.bi-pair-time { font-size:.8rem; font-weight:600; color:rgba(255,255,255,0.75); }
.bi-pair-badge {
    font-size:.65rem; font-weight:700; padding:.15rem .5rem; border-radius:9999px;
    background:rgba(34,197,94,0.15); border:1px solid rgba(34,197,94,0.3); color:#4ade80;
}
.bi-pair-legs { display:flex; align-items:center; gap:.5rem; flex-wrap:wrap; }
.bi-pair-leg  { display:flex; align-items:center; gap:.35rem; font-size:.72rem; }
.bi-pair-leg--green  { color:rgba(255,255,255,0.45); }
.bi-pair-leg--purple { color:rgba(255,255,255,0.45); }
.bi-pair-sep  { width:.25rem; height:.25rem; border-radius:50%; background:rgba(255,255,255,0.2); }
.bi-pair-reward { text-align:right; flex-shrink:0; }
.bi-pair-amt  { font-size:1rem; font-weight:800; color:#4ade80; }
.bi-pair-cnt  { font-size:.68rem; color:rgba(255,255,255,0.35); margin-top:.1rem; }

/* ── quick actions ──────────────────────────────────────────────── */
.bi-qa-nav  { display:flex; flex-direction:column; gap:6px; padding:0 14px 14px; }
.bi-qa-item {
    display:flex; align-items:center; gap:.875rem;
    padding:.875rem; border-radius:.875rem; text-decoration:none;
    border:1px solid; transition:all .2s;
}
.bi-qa-item--blue   { background:rgba(59,130,246,0.06);  border-color:rgba(59,130,246,0.18); }
.bi-qa-item--blue:hover { background:rgba(59,130,246,0.14); border-color:rgba(59,130,246,0.38); }
.bi-qa-item--purple { background:rgba(168,85,247,0.06);  border-color:rgba(168,85,247,0.18); }
.bi-qa-item--purple:hover { background:rgba(168,85,247,0.14); border-color:rgba(168,85,247,0.38); }
.bi-qa-item--green  { background:rgba(34,197,94,0.06);   border-color:rgba(34,197,94,0.18); }
.bi-qa-item--green:hover  { background:rgba(34,197,94,0.14);  border-color:rgba(34,197,94,0.38); }
.bi-qa-icon {
    width:2.25rem; height:2.25rem; flex-shrink:0; border-radius:.625rem;
    display:flex; align-items:center; justify-content:center;
}
.bi-qa-item--blue   .bi-qa-icon { background:rgba(59,130,246,0.15);  border:1px solid rgba(59,130,246,0.3);  color:#60a5fa; }
.bi-qa-item--purple .bi-qa-icon { background:rgba(168,85,247,0.15);  border:1px solid rgba(168,85,247,0.3);  color:#c084fc; }
.bi-qa-item--green  .bi-qa-icon { background:rgba(34,197,94,0.12);   border:1px solid rgba(34,197,94,0.3);   color:#4ade80; }
.bi-qa-icon svg { width:1rem; height:1rem; }
.bi-qa-text { flex:1; }
.bi-qa-text h4 { font-size:.85rem; font-weight:700; color:#fff; margin-bottom:.1rem; }
.bi-qa-text p  { font-size:.72rem; color:rgba(255,255,255,0.4); }
.bi-qa-arrow { width:1rem; height:1rem; color:rgba(255,255,255,0.25); transition:transform .2s; }
.bi-qa-item:hover .bi-qa-arrow { transform:translateX(3px); color:rgba(255,255,255,0.6); }
</style>
